import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { SiteNav } from "../components/SiteNav";
import SiteFooter from '../components/SiteFooter';

const INK = "#2A1E5C";
const INK2 = "#5D5A6B";
const MUTED = "#8B7E6A";
const BG = "#FDFBF7";
const GOLD = "#F5CB5C";

const SECTIONS = [
  { id: "unstructured-income", label: "Unstructured Income in the New Work Economy" },
  { id: "adhd-neurodivergent", label: "Built for the ADHD and Neurodivergent Mind" },
  { id: "relay-target", label: "The Relay Target" },
  { id: "relay-race", label: "The Relay Race" },
  { id: "payday-rhythm", label: "Payday Rhythm" },
  { id: "flexibility", label: "Flexibility Within Scaffolding" },
  { id: "work-rest-agency", label: "Work, Rest, and Agency" },
  { id: "the-tools", label: "The Tools" },
  { id: "what-its-not", label: "What the Method Is Not" },
  { id: "ownership", label: "Ownership" },
];

const H2 = ({ id, children }) => (
  <h2 id={id} className="text-2xl sm:text-3xl mt-12 mb-4 scroll-mt-24" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
    {children}
  </h2>
);
const P = ({ children }) => (
  <p className="text-base leading-relaxed mb-4" style={{ color: INK2 }}>{children}</p>
);
const LI = ({ children }) => (
  <li className="text-base leading-relaxed" style={{ color: INK2 }}>{children}</li>
);

const TocLinks = ({ onNavigate }) => (
  <ul className="space-y-2">
    {SECTIONS.map((s) => (
      <li key={s.id}>
        <a
          href={`#${s.id}`}
          onClick={onNavigate}
          className="block text-[13px] leading-snug transition-colors hover:underline"
          style={{ color: INK2 }}
          data-testid={`method-toc-${s.id}`}
        >
          {s.label}
        </a>
      </li>
    ))}
  </ul>
);

export default function PaydayRelayMethodPage() {
  const { hash } = useLocation();
  useEffect(() => {
    document.title = "Payday Relay Method";
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        // Wait a tick so the DOM is fully painted before scrolling
        requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [hash]);

  return (
    <div className="min-h-screen" style={{ background: BG }}>
      <SiteNav />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 lg:grid lg:grid-cols-[230px_1fr] lg:gap-12">
        {/* Sticky side Table of Contents (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-8 pt-10" data-testid="method-toc-desktop">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: MUTED }}>
              On this page
            </p>
            <TocLinks />
          </div>
        </aside>

        <main className="max-w-3xl pb-20" data-testid="method-page">
          <p className="text-xs font-bold uppercase tracking-widest mt-8 mb-2" style={{ color: MUTED }}>
            The rules of the race
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }} data-testid="method-title">
            Payday Relay Method<sup className="text-[0.45em] align-super"></sup>
          </h1>
          <p className="text-xs italic mb-8" style={{ color: MUTED }} data-testid="method-ip-line">
            Payday Relay Method is a proprietary educational framework owned by Earthwise Digital LLC.
          </p>

          {/* Table of Contents (mobile) */}
          <div className="lg:hidden rounded-2xl p-4 mb-8 border" style={{ background: "#FFFFFF", borderColor: "#E8E2D5" }} data-testid="method-toc-mobile">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: MUTED }}>
              On this page
            </p>
            <TocLinks />
          </div>

          <P>The Payday Relay Method is a structured approach to managing unstructured income.</P>
          <P>
            It was created for freelancers, contractors, creators, gig workers, platform workers, self-employed
            people, and independent earners whose work, income, and schedule are not organized by an employer's
            built-in structure.
          </P>
          <P>
            Traditional financial planning often starts far away: five-year plans, ten-year plans, retirement
            numbers, annual projections, and large goals with a thousand invisible steps between here and there.
          </P>
          <P><b style={{ color: INK }}>The Payday Relay Method starts where you are.</b></P>
          <P>
            It brings the big picture down to the micro level: how much you need this year, how much you need
            this week, and what that means for today.
          </P>

          <H2 id="unstructured-income">Unstructured Income in the New Work Economy</H2>
          <P>
            In the Payday Relay Method, unstructured income means money earned without built-in workday
            boundaries. Today's gig, platform, and side-hustle economies offer unprecedented freedom.
            Freelancers, creators, and contractors are leaving traditional corporate roles to build stability on
            their own terms, instead of depending entirely on employers who may never be as invested in them as
            they are in themselves.
          </P>
          <P>
            That independence can be powerful. But independence without structure can quietly become another
            trap. Many people do not leave corporate, traditional employment, or fixed-schedule work because they
            want to work 24/7. They leave because they want more agency, more control, more flexibility, and more
            room to build a life that actually fits them. But for self-directed earners, the workday often has no
            natural edge. The work may be there. The clients may be there. The platform may be open. The rides,
            projects, deliveries, or tasks may already be waiting in the queue. The missing piece is not always
            opportunity. The missing piece is structure.
          </P>
          <P>
            Payday Relay is built for people who are already in motion: freelancers with a full client load,
            contractors managing steady projects, platform workers with work available, creators with paid
            opportunities, self-employed people who find themselves unexpectedly busy, and new business owners who
            have crossed the first major hurdle of getting work and now need a clearer way to manage it. These are
            people who understand that structure is not a luxury. It is what makes the work sustainable.
          </P>
          <P>
            For self-directed earners, the absence of structure creates its own pressure. There may be no manager
            setting the schedule, no clock-in/clock-out rhythm, no paycheck cycle that marks what has already been
            covered, and no external signal that says, "You've done enough. You can stop." That pressure can be
            especially sharp for neurodivergent and ADHD brains, where unstructured time, open-ended work, task
            initiation, stopping points, transitions, and self-directed pacing can all become part of the same
            knot.
          </P>
          <P>
            That is one of the reasons Payday Relay exists. The relay target gives the workday a finish line. It
            turns "I could work all day" into "Here is what today needs from me." It helps replace
            start-whenever/end-when-you-burn-out with a weekly rhythm, a daily target, and a clearer handoff from
            work to rest.
          </P>

          <H2 id="adhd-neurodivergent">Built for the ADHD and Neurodivergent Mind</H2>
          <P>
            The Payday Relay Method was built with the ADHD and neurodivergent mind in mind because that is part
            of the founder's lived experience. Before Payday Relay, the problem was not only earning money. It was
            orientation. Without a clear structure, work could feel like a treadmill: earn to survive, keep going
            because there is always more available, stop only when exhaustion finally wins, then start again
            without a clear sense of what the work was building toward.
          </P>
          <P>
            For an ADHD brain, far-off financial planning can feel too abstract to hold. Five-year plans, ten-year
            plans, retirement numbers, and giant savings goals can become an overwhelming baseline. The goal may
            matter, but the path is too blurry to act on today. Payday Relay starts smaller. Not because the goal
            is small, but because the entry point has to be usable.
          </P>
          <P>
            The method asks: "What can I build this year, from where I actually am?" Then it turns that answer
            into a weekly relay target and a daily focus number. The point is not to pressure the brain into
            constant productivity. The point is to reduce ambiguity, create a visible next step, and give work a
            rhythm. When the year feels too big, return to the week. When the week feels too big, return to today.
            That is the ADHD-friendly structure at the heart of the method: build from where you are, focus on the
            next relay, pass the baton to your next self, and create momentum through rhythm instead of overwhelm.
          </P>

          <H2 id="relay-target">The Relay Target</H2>
          <P>
            A relay target is the weekly earning target that turns a larger income goal into a trackable rhythm.
            Instead of only asking, "How much money do I need this year?" the method also asks: "How much do I
            need this week?" The relay target can include income needs, tax reserves, debt payoff goals, savings
            goals, PTO set-asides, and the number of weeks a person realistically plans to work. The weekly target
            can then be broken into a daily target. That daily number is not meant to create pressure for
            pressure's sake. It is meant to create clarity, grounding your immediate attention right where you
            are.
          </P>

          <H2 id="relay-race">The Relay Race</H2>
          <P>
            Payday Relay uses the rhythm of a relay race because self-employment is not one dramatic sprint. It is
            a series of handoffs. Each week, you run the relay in front of you. You track what came in. You see
            whether you cleared the target, missed it, or created a surplus. Then you pass the baton to your next
            self. The goal is not to compete with someone else's numbers. The goal is to create a relationship
            with your own numbers. You learn your pace. You notice your patterns. You create personal bests. You
            build rhythm through repetition instead of waiting for one perfect plan to solve everything at once.
          </P>

          <H2 id="payday-rhythm">Payday Rhythm</H2>
          <P>
            For many independent workers, earning money and receiving money are not the same event. Payday rhythm
            is the gap between when work is completed or income is earned and when the money actually arrives.
            This is part of the scaffolding of the Payday Relay Method because independent earners often do not
            have an employer assigning a fixed payday. The method helps you choose a payday rhythm for yourself,
            stick to it, and plan around it instead of reacting to every deposit as if it exists in isolation.
            Payday rhythm turns "money came in" into "money has a place in the relay."
          </P>

          <H2 id="flexibility">Flexibility Within Scaffolding</H2>
          <P>
            Payday Relay is structured, but it is not rigid. The method uses quarterly checkpoints because
            self-employed life changes. Client load changes. Capacity changes. Family needs change. Energy
            changes. Rates change. Goals change. Sometimes you want to take on more work. Sometimes you need to
            take on less. Sometimes the version of the plan that made sense in January no longer fits by July. The
            quarterly structure gives you room to adjust without abandoning the whole system. You are not locked
            into a fantasy version of your life. You are invited into an ongoing conversation with yourself about
            what is reasonable, what is possible, and what kind of work rhythm you actually want to build.
          </P>

          <H2 id="work-rest-agency">Work, Rest, and Agency</H2>
          <P>
            The Payday Relay Method is not only about making more money. It is about creating more agency around
            work, money, rest, and attention. For independent earners, the absence of structure can make work feel
            endless. There is always more you could do, more you could pitch, more you could earn, more you could
            fix. The relay target creates a finish line. The payday rhythm creates a landing point. The quarterly
            reset creates room to adapt. The reflection process creates a conversation with yourself. Together,
            these pieces help you engage your work creatively without letting it swallow your whole life.
          </P>

          <H2 id="the-tools">The Tools</H2>
          <P>
            Payday Relay tools may include the Relay Calculator, the Payday Relay Google Sheet, the Payday Relay
            mobile app, worksheets, coaching prompts, reflection tools, and related resources. Each tool is
            designed to support the same core method:
          </P>
          <ol className="list-decimal pl-6 space-y-2 mb-4">
            <LI>Map the income goal</LI>
            <LI>Set the relay target</LI>
            <LI>Break the target into daily focus</LI>
            <LI>Track the current week</LI>
            <LI>Respect the payday rhythm</LI>
            <LI>Set aside for taxes, savings, debt payoff, and PTO</LI>
            <LI>Reflect and reset</LI>
            <LI>Build a repeatable rhythm</LI>
          </ol>

          <H2 id="what-its-not">What the Method Is Not</H2>
          <P>
            The Payday Relay Method is not financial advice, tax advice, investment advice, legal advice, or a
            promise of income. It does not replace a CPA, attorney, financial planner, bookkeeper, therapist, or
            business advisor. It is an educational structure for helping independent earners see their numbers,
            reduce ambiguity, and build a healthier rhythm around money and work.
          </P>

          <H2 id="ownership">Ownership</H2>
          <P>
            Payday Relay, Payday Relay Method, the Payday Relay mobile app, Relay Calculator, relay target, payday
            rhythm, and related language, frameworks, workflows, designs, worksheets, and product materials are
            owned by Earthwise Digital LLC.
          </P>
          <P>
            The Payday Relay Method may be discussed, taught, and shared by Earthwise Digital LLC through its
            products, services, content, and educational materials. No part of the method, tools, worksheets,
            formulas, copy, product structure, or related materials may be copied, republished, reverse
            engineered, resold, redistributed, or used to create a competing product without written permission.
          </P>

          <div className="mt-14 text-center">
            <Link
              to="/payday-relay"
              className="inline-block px-8 py-4 rounded-full font-bold text-lg transition-transform hover:scale-[1.02]"
              style={{ background: INK, color: "#fff" }}
              data-testid="method-bottom-cta"
            >
              Run your race with the Payday Relay Method →
            </Link>
          </div>
        </main>
      </div>

      <SiteFooter />
    </div>
  );
}
